import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { PieChart, Pie, Cell, ResponsiveContainer } from 'recharts';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/Button';
import '../styles/PortfolioPage.css';

interface AssetStat {
  Symbol: string;
  Weight: number;
  AssetValue: number;
  Sector: string;
  PnL: number;
  PnLPct: number;
  ChangePct1D: number;
  ChangeDol1D: number;
  Name: string;
  InstrumentType: string;
  Industry: string;
}

interface Portfolio {
  tag: string;
  portId: string;
  NAV: number;
  YTD_Return: number;
  Score: number;
  clientAlign: {
    investGoal: string;
    investmentHorizon: number;
    initialInvest: number;
    accountType: string;
  };
  x1Day_ReturnsPct: number;
  x1Day_ReturnsDollar: number;
  x1Week_ReturnsPct: number;
  x1Week_ReturnsDollar: number;
  SinceWLAdded_Return: number;
  AnnualizedRet: number;
  AnnualizedVol: number;
  MaximumDrawDown: number;
  historicalVaR: number;
  historicalCVaR: number;
  SecurityDrift: number;
  SectorDrift: number;
  AssetClassDrift: number;
  IndustryDrift: number;
  AssetStats: AssetStat[];
  createDate: string;
  LastRefresh: string;
  portStatus: string;
}

interface LocationState {
  portfolio?: Portfolio;
}

const PortfolioPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  useAuth(); // Keep the hook call to maintain authentication context
  const [activeTab, setActiveTab] = useState('performance');
  const [portfolio, setPortfolio] = useState<Portfolio | null>(null);

  // Colors for pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];


  useEffect(() => {
    const state = location.state as LocationState;
    if (state?.portfolio) {
      const portfolio = state.portfolio;
      setPortfolio(portfolio);
      
      // Update URL with correct portfolio ID
      if (portfolio.portId && !id) {
        navigate(`/portfolio/${portfolio.portId}`, { 
          state: { portfolio },
          replace: true 
        });
      }
    } else {
      // No portfolio data, redirect to welcome page
      navigate('/welcome');
    }
  }, [id, location.state, navigate]);

  return (
    <div className="portfolio-page">
      <div className="portfolio-header" style={{ marginBottom: '2rem' }}>
        <h1>{portfolio?.tag || 'Portfolio Analysis'}</h1>
        <div className="portfolio-stats">
          <div className="stat">
            <label>Total Value</label>
            <span>${portfolio?.NAV?.toLocaleString() || 0}</span>
          </div>
          <div className="stat">
            <label>Performance (YTD)</label>
            <span className={(portfolio?.YTD_Return ?? 0) > 0 ? 'positive' : 'negative'}>
              {((portfolio?.YTD_Return ?? 0) * 100).toFixed(2)}%
            </span>
          </div>
          <div className="stat">
            <label>Risk Score</label>
            <span>{((portfolio?.Score || 0) * 100).toFixed(0)}</span>
          </div>
        </div>
      </div>

      <div className="portfolio-tabs" style={{ 
        display: 'flex', 
        gap: '1rem', 
        marginBottom: '1.5rem',
        backgroundColor: '#f8fafc',
        padding: '0.75rem',
        borderRadius: '0.5rem'
      }}>
        <Button 
          onClick={() => setActiveTab('performance')}
          active={activeTab === 'performance'}
        >
          Performance
        </Button>
        <Button 
          onClick={() => setActiveTab('allocation')}
          active={activeTab === 'allocation'}
        >
          Asset Allocation
        </Button>
        <Button 
          onClick={() => setActiveTab('risk')}
          active={activeTab === 'risk'}
        >
          Risk Analysis
        </Button>
      </div>

      <div className="portfolio-content" style={{ backgroundColor: 'white', padding: '1.5rem', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)' }}>
        {activeTab === 'performance' && (
          <div className="performance-section">
            {/* Portfolio Overview */}
            <div style={{ marginBottom: '2rem', padding: '1rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Portfolio Overview</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Investment Goal</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.clientAlign?.investGoal}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Investment Horizon</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.clientAlign?.investmentHorizon} years</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Initial Investment</div>
                  <div style={{ fontWeight: '500' }}>${portfolio?.clientAlign?.initialInvest?.toLocaleString()}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Account Type</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.clientAlign?.accountType}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Created Date</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.createDate}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Last Updated</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.LastRefresh}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Status</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.portStatus}</div>
                </div>
              </div>
            </div>

            {/* Performance Metrics */}
            <div style={{ marginBottom: '2rem' }}>
              <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Performance Summary</h3>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', 
                gap: '1rem',
                backgroundColor: 'white',
                padding: '1rem',
                borderRadius: '0.5rem',
                boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
              }}>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Today</div>
                  <div style={{ 
                    color: (portfolio?.x1Day_ReturnsPct ?? 0) > 0 ? '#10B981' : '#EF4444',
                    fontWeight: '500'
                  }}>
                    {portfolio?.x1Day_ReturnsPct?.toFixed(2)}%
                    <br />
                    ${portfolio?.x1Day_ReturnsDollar?.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>This Week</div>
                  <div style={{ 
                    color: (portfolio?.x1Week_ReturnsPct ?? 0) > 0 ? '#10B981' : '#EF4444',
                    fontWeight: '500'
                  }}>
                    {portfolio?.x1Week_ReturnsPct?.toFixed(2)}%
                    <br />
                    ${portfolio?.x1Week_ReturnsDollar?.toFixed(2)}
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>YTD</div>
                  <div style={{ 
                    color: (portfolio?.YTD_Return ?? 0) > 0 ? '#10B981' : '#EF4444',
                    fontWeight: '500'
                  }}>
                    {((portfolio?.YTD_Return ?? 0) * 100).toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Since Added</div>
                  <div style={{ 
                    color: (portfolio?.SinceWLAdded_Return ?? 0) > 0 ? '#10B981' : '#EF4444',
                    fontWeight: '500'
                  }}>
                    {((portfolio?.SinceWLAdded_Return ?? 0) * 100).toFixed(2)}%
                  </div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Annualized Return</div>
                  <div style={{ 
                    color: (portfolio?.AnnualizedRet ?? 0) > 0 ? '#10B981' : '#EF4444',
                    fontWeight: '500'
                  }}>
                    {portfolio?.AnnualizedRet?.toFixed(2)}%
                  </div>
                </div>
              </div>
            </div>

            {/* Holdings Table */}
            <div className="performance-metrics">
              <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Holdings Detail</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: 'white', borderRadius: '0.5rem', overflow: 'hidden' }}>
                <thead style={{ backgroundColor: '#f8fafc' }}>
                  <tr>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Symbol</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Name</th>
                    <th style={{ padding: '1rem', textAlign: 'left' }}>Type</th>
                    <th style={{ padding: '1rem', textAlign: 'right' }}>Value</th>
                    <th style={{ padding: '1rem', textAlign: 'right' }}>Weight</th>
                    <th style={{ padding: '1rem', textAlign: 'right' }}>Today's Change</th>
                  </tr>
                </thead>
                <tbody>
                  {portfolio?.AssetStats?.map((asset: AssetStat) => (
                    <tr key={asset.Symbol} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '1rem', fontWeight: '500' }}>{asset.Symbol}</td>
                      <td style={{ padding: '1rem' }}>{asset.Name}</td>
                      <td style={{ padding: '1rem' }}>{asset.InstrumentType}</td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>${asset.AssetValue?.toLocaleString()}</td>
                      <td style={{ padding: '1rem', textAlign: 'right' }}>{asset.Weight?.toFixed(2)}%</td>
                      <td style={{ 
                        padding: '1rem', 
                        textAlign: 'right',
                        color: asset.ChangePct1D >= 0 ? '#10B981' : '#EF4444'
                      }}>
                        {typeof asset.ChangePct1D === 'number' ? asset.ChangePct1D.toFixed(2) : '0.00'}% 
                        (${typeof asset.ChangeDol1D === 'number' ? asset.ChangeDol1D.toFixed(2) : '0.00'})
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'allocation' && (
          <div className="allocation-section">
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '2rem' }}>
              {/* Pie Chart */}
              <div style={{ 
                backgroundColor: '#f8fafc', 
                padding: '2rem', 
                borderRadius: '0.5rem',
                height: '500px',
                minWidth: '500px'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Asset Allocation</h3>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={portfolio?.AssetStats?.map((asset: AssetStat) => ({
                        name: asset.Symbol,
                        value: asset.Weight
                      }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={80}
                      outerRadius={160}
                      paddingAngle={2}
                      dataKey="value"
                      nameKey="name"
                      label={({ name, value }) => `${name} (${value.toFixed(2)}%)`}
                    >
                      {portfolio?.AssetStats?.map((entry: AssetStat, index: number) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>

              {/* Holdings by Sector */}
              <div style={{ 
                backgroundColor: '#f8fafc', 
                padding: '1.5rem', 
                borderRadius: '0.5rem'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Holdings by Sector</h3>
                <div style={{ 
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem'
                }}>
                  {Object.entries(
                    portfolio?.AssetStats?.reduce((acc: Record<string, number>, asset: AssetStat) => {
                      const sector = asset.Sector;
                      if (!acc[sector]) acc[sector] = 0;
                      acc[sector] += asset.Weight;
                      return acc;
                    }, {}) || {}
                  )
                    .sort(([, a], [, b]) => b - a)
                    .map(([sector, weight], index) => (
                      <div key={sector} style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.5rem',
                        backgroundColor: index % 2 === 0 ? 'white' : 'transparent',
                        borderRadius: '0.25rem'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem'
                        }}>
                          <div style={{ 
                            width: '12px', 
                            height: '12px', 
                            backgroundColor: COLORS[index % COLORS.length],
                            borderRadius: '2px'
                          }}></div>
                          <span>{sector}</span>
                        </div>
                        <span style={{ fontWeight: '500' }}>{weight.toFixed(2)}%</span>
                      </div>
                    ))}
                </div>
              </div>

              {/* Holdings List */}
              <div style={{ 
                gridColumn: '1 / -1',
                backgroundColor: '#f8fafc', 
                padding: '1.5rem', 
                borderRadius: '0.5rem'
              }}>
                <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Holdings Detail</h3>
                <div style={{ 
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                  gap: '1rem'
                }}>
                  {portfolio?.AssetStats?.sort((a, b) => b.Weight - a.Weight)
                    .map((asset: AssetStat, index: number) => (
                      <div key={asset.Symbol} style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        backgroundColor: 'white',
                        borderRadius: '0.5rem',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          gap: '0.5rem'
                        }}>
                          <div style={{ 
                            width: '8px', 
                            height: '8px', 
                            backgroundColor: COLORS[index % COLORS.length],
                            borderRadius: '2px'
                          }}></div>
                          <div>
                            <div style={{ fontWeight: '500' }}>{asset.Symbol}</div>
                            <div style={{ fontSize: '0.75rem', color: '#64748b' }}>{asset.Industry}</div>
                          </div>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <div style={{ fontWeight: '500' }}>{asset.Weight.toFixed(2)}%</div>
                          <div style={{ fontSize: '0.75rem', color: '#64748b' }}>${asset.AssetValue.toLocaleString()}</div>
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'risk' && (
          <div className="risk-section">
            <h3 style={{ marginBottom: '1rem', color: '#1e1e1e' }}>Risk Analysis</h3>
            
            {/* Risk Metrics */}
            <div style={{ marginBottom: '2rem' }}>
              <div className="risk-metrics" style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem' 
              }}>
                <div className="risk-metric" style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
                  <h4 style={{ color: '#475569', marginBottom: '0.5rem' }}>Volatility</h4>
                  <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#334155' }}>
                    {portfolio?.AnnualizedVol?.toFixed(2)}%
                  </span>
                </div>
                <div className="risk-metric" style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
                  <h4 style={{ color: '#475569', marginBottom: '0.5rem' }}>Max Drawdown</h4>
                  <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#334155' }}>
                    {portfolio?.MaximumDrawDown?.toFixed(2)}%
                  </span>
                </div>
                <div className="risk-metric" style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
                  <h4 style={{ color: '#475569', marginBottom: '0.5rem' }}>Historical VaR</h4>
                  <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#334155' }}>
                    {portfolio?.historicalVaR?.toFixed(2)}%
                  </span>
                </div>
                <div className="risk-metric" style={{ textAlign: 'center', padding: '1.5rem', backgroundColor: '#f8fafc', borderRadius: '0.5rem' }}>
                  <h4 style={{ color: '#475569', marginBottom: '0.5rem' }}>Historical CVaR</h4>
                  <span style={{ fontSize: '1.25rem', fontWeight: '600', color: '#334155' }}>
                    {portfolio?.historicalCVaR?.toFixed(2)}%
                  </span>
                </div>
              </div>
            </div>

            {/* Drift Analysis */}
            <div>
              <h4 style={{ marginBottom: '1rem', color: '#475569' }}>Drift Analysis</h4>
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', 
                gap: '1rem',
                backgroundColor: 'white',
                padding: '1rem',
                borderRadius: '0.5rem'
              }}>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Security Drift</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.SecurityDrift?.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Sector Drift</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.SectorDrift?.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Asset Class Drift</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.AssetClassDrift?.toFixed(2)}</div>
                </div>
                <div>
                  <div style={{ color: '#64748b', marginBottom: '0.25rem' }}>Industry Drift</div>
                  <div style={{ fontWeight: '500' }}>{portfolio?.IndustryDrift?.toFixed(2)}</div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PortfolioPage;
